import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown", "chipZone", "composerInner"]
  static values  = { orgSlug: String }

  connect() {
    this.activeIndex = -1
    this._searchTimer = null
    this._mentionActive = false
    this._handleClickOutside = this._clickOutside.bind(this)
    document.addEventListener("click", this._handleClickOutside)

    // Chip is sticky-until-changed: it persists across sends. Server re-renders
    // the composer after each turn with the chip pre-populated from
    // chat.other_data['active_page_id'], so client-side clearing is unnecessary
    // (and would fight the server render). To detach, user clicks the pill ✕.
    // If a chip was server-rendered, re-measure its width on connect so the
    // textarea's --chip-inset gets set correctly.
    this._recomputeChipInset()
  }

  disconnect() {
    document.removeEventListener("click", this._handleClickOutside)
    if (this._searchTimer) clearTimeout(this._searchTimer)
  }

  _recomputeChipInset() {
    if (!this.hasChipZoneTarget || !this.hasComposerInnerTarget) return
    const chip = this.chipZoneTarget.querySelector(".laz-page-chip")
    if (!chip) {
      this.composerInnerTarget.style.setProperty("--chip-inset", "0")
      return
    }
    requestAnimationFrame(() => {
      const w = chip.getBoundingClientRect().width
      const inset = w > 0 ? `${Math.round(w + 8)}px` : "0"
      this.composerInnerTarget.style.setProperty("--chip-inset", inset)
    })
  }

  onInput() {
    const input = this.inputTarget
    const val = input.value
    const pos = input.selectionStart

    // Find the last @ before cursor that isn't already closed with ]
    const before = val.slice(0, pos)
    const atIdx = before.lastIndexOf("@")

    if (atIdx === -1 || before.slice(atIdx).includes("]")) {
      this._hide()
      return
    }

    const query = before.slice(atIdx + 1).trim()
    this._mentionActive = true

    if (query.length < 2) {
      this._showEmpty()
      return
    }

    this._search(query)
  }

  onKeydown(event) {
    if (!this._mentionActive) return

    const items = this.dropdownTarget.querySelectorAll("[data-mention-item]")

    if (event.key === "Escape") {
      event.preventDefault()
      this._hide()
      return
    }

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.activeIndex = Math.min(this.activeIndex + 1, items.length - 1)
      this._highlightItem(items)
      return
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      this.activeIndex = Math.max(this.activeIndex - 1, 0)
      this._highlightItem(items)
      return
    }

    if (event.key === "Enter" && this.activeIndex >= 0 && items.length > 0) {
      event.preventDefault()
      event.stopPropagation()
      const item = items[this.activeIndex]
      this._selectFromEl(item)
      return
    }
  }

  select(event) {
    this._selectFromEl(event.currentTarget)
  }

  _selectFromEl(el) {
    if (el.dataset.mentionType === "page") {
      this._selectPage(el.dataset.display, el.dataset.pageId)
    } else {
      this._selectItem(el.dataset.display, el.dataset.bqName)
    }
  }

  // Private

  _search(query) {
    if (this._searchTimer) clearTimeout(this._searchTimer)

    this._searchTimer = setTimeout(async () => {
      try {
        // Fire both searches in parallel — pages usually respond first (much smaller index)
        const [pagesResp, entitiesResp] = await Promise.all([
          fetch(`/${this.orgSlugValue}/chats/page_search?q=${encodeURIComponent(query)}`),
          fetch(`/${this.orgSlugValue}/entities/search?q=${encodeURIComponent(query)}`)
        ])
        const pages = pagesResp.ok ? await pagesResp.json() : []
        const entities = entitiesResp.ok ? await entitiesResp.json() : []
        this._renderResults(pages, entities)
      } catch (e) {
        this._hide()
      }
    }, 300)
  }

  _renderResults(pages, entities) {
    this.activeIndex = -1
    const dd = this.dropdownTarget

    if (pages.length === 0 && entities.length === 0) {
      dd.innerHTML = `<div class="px-4 py-3 text-sm text-gray-400">No matching pages or records</div>`
      dd.classList.remove("hidden")
      return
    }

    let html = ""

    // Pages first — this is the anchor context for page-scoped analysis
    if (pages.length > 0) {
      html += `<div class="px-4 py-1.5 text-[11px] font-medium text-gray-400 uppercase tracking-wider">Pages</div>`
      pages.forEach(page => {
        html += `<div data-mention-item data-mention-type="page"
                      data-display="${this._escapeAttr(page.page_name)}"
                      data-page-id="${this._escapeAttr(page.id)}"
                      data-action="click->chat-mention#select"
                      class="flex items-center gap-3 px-4 py-2 mx-2 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors">
          <div class="w-6 h-6 flex items-center justify-center bg-purple-50 rounded-md flex-shrink-0">
            <span class="text-purple-600"><i data-lucide="file-text" class="w-3 h-3 inline"></i></span>
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm text-gray-700 truncate">${this._escapeHtml(page.page_name)}</div>
            <div class="text-[11px] text-gray-400 truncate">${this._escapeHtml(page.template_name)}</div>
          </div>
        </div>`
      })
    }

    // Then entities/tables — grouped by table_name (existing behavior)
    if (entities.length > 0) {
      const grouped = {}
      entities.forEach(r => {
        if (!grouped[r.table_name]) grouped[r.table_name] = []
        grouped[r.table_name].push(r)
      })
      for (const [tableName, items] of Object.entries(grouped)) {
        html += `<div class="px-4 py-1.5 text-[11px] font-medium text-gray-400 uppercase tracking-wider">${this._escapeHtml(tableName)}</div>`
        items.forEach(item => {
          html += `<div data-mention-item data-mention-type="entity"
                        data-display="${this._escapeAttr(item.display)}"
                        data-bq-name="${this._escapeAttr(item.bq_name)}"
                        data-action="click->chat-mention#select"
                        class="flex items-center gap-3 px-4 py-2 mx-2 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors">
            <div class="w-6 h-6 flex items-center justify-center bg-emerald-50 rounded-md flex-shrink-0">
              <span class="text-emerald-600"><i data-lucide="database" class="w-3 h-3 inline"></i></span>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-sm text-gray-700 truncate">${this._escapeHtml(item.display)}</div>
            </div>
          </div>`
        })
      }
    }

    dd.innerHTML = html
    dd.classList.remove("hidden")
  }

  _showEmpty() {
    const dd = this.dropdownTarget
    dd.innerHTML = `<div class="px-4 py-3 text-sm text-gray-400">Type to search records...</div>`
    dd.classList.remove("hidden")
  }

  _hide() {
    this._mentionActive = false
    this.activeIndex = -1
    if (this._searchTimer) clearTimeout(this._searchTimer)
    this.dropdownTarget.classList.add("hidden")
  }

  _selectItem(display, bqName) {
    const input = this.inputTarget
    const val = input.value
    const pos = input.selectionStart
    const before = val.slice(0, pos)
    const atIdx = before.lastIndexOf("@")

    if (atIdx === -1) return

    const after = val.slice(pos)
    const mention = `@${display} [${bqName}]`
    input.value = val.slice(0, atIdx) + mention + " " + after
    input.focus()

    const newPos = atIdx + mention.length + 1
    input.setSelectionRange(newPos, newPos)

    this._hide()
  }

  // Page selection is a CONTEXT tag, not a table reference — so we:
  //   1. strip the `@query` fragment out of the textarea
  //   2. render a purple pill in the chip zone that floats over the textarea's
  //      leading padding, so the visible question text starts to the right of it
  //   3. write the page id into the hidden form field
  //   4. wire the pill's ✕ button to reverse (2) and (3)
  // Only one chip at a time — selecting a second page swaps the current one.
  _selectPage(display, pageId) {
    const input = this.inputTarget
    const val = input.value
    const pos = input.selectionStart
    const before = val.slice(0, pos)
    const atIdx = before.lastIndexOf("@")
    if (atIdx === -1) return

    // Strip the `@query` fragment; do NOT insert `@display` text
    const after = val.slice(pos)
    input.value = val.slice(0, atIdx) + after
    input.focus()
    input.setSelectionRange(atIdx, atIdx)
    input.dispatchEvent(new Event("input", { bubbles: true }))

    // Set backend hidden field
    this._setHiddenPageId(pageId)

    // Render the chip pill
    this._renderChip(display, pageId)

    this._hide()
  }

  _renderChip(display, pageId) {
    const zone = this.hasChipZoneTarget ? this.chipZoneTarget : null
    if (!zone) return

    zone.innerHTML = ""
    const chip = document.createElement("span")
    chip.className = "laz-page-chip"
    chip.dataset.pageId = pageId
    chip.innerHTML = `
      <svg class="laz-chip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
        <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
        <polyline points="14 3 14 9 20 9"></polyline>
      </svg>
      <span class="laz-chip-name">${this._escapeHtml(display)}</span>
      <button type="button" class="laz-chip-remove" title="Remove page context" data-action="click->chat-mention#removeChip">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    `
    zone.appendChild(chip)

    // After the chip is laid out, measure its width and push the textarea's
    // text over so it starts to the right of the pill on line 1.
    requestAnimationFrame(() => {
      const w = chip.getBoundingClientRect().width
      const inset = w > 0 ? `${Math.round(w + 8)}px` : "0"
      if (this.hasComposerInnerTarget) {
        this.composerInnerTarget.style.setProperty("--chip-inset", inset)
      }
    })
  }

  removeChip(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    this._clearChip()
    if (this.hasInputTarget) this.inputTarget.focus()
  }

  _clearChip() {
    if (this.hasChipZoneTarget) this.chipZoneTarget.innerHTML = ""
    if (this.hasComposerInnerTarget) {
      this.composerInnerTarget.style.setProperty("--chip-inset", "0")
    }
    this._setHiddenPageId("")
  }

  _setHiddenPageId(pageId) {
    const form = this.hasInputTarget ? this.inputTarget.closest("form") : null
    if (!form) return
    let hidden = form.querySelector("input[name='message[mentioned_page_id]']")
    if (!hidden) {
      hidden = document.createElement("input")
      hidden.type = "hidden"
      hidden.name = "message[mentioned_page_id]"
      form.appendChild(hidden)
    }
    hidden.value = pageId
  }

  _highlightItem(items) {
    items.forEach((item, i) => {
      item.classList.toggle("bg-purple-50", i === this.activeIndex)
    })
  }

  _clickOutside(event) {
    if (!this._mentionActive) return
    if (this.element.contains(event.target)) return
    this._hide()
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  _escapeAttr(str) {
    return str.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
